import React from 'react';
import { useIntl } from 'react-intl';
import { faPlus } from '@fortawesome/pro-light-svg-icons/faPlus';
import type { ReleaseThemeId } from 'fym-shared/src/music24/releaseThemes';
import { TypedMemo } from '../../../../common/types';
import { SMLButton } from '../../../../common/views/SMLButton/SMLButton';
import { useSMLRouter } from '../../../../../routing/sml/hooks';
import { SMLRoutes } from '../../../../../routing/sml/SMLRoutes';
import { useOpenAddSong } from '../../../../smartLinks/catalog/addSong/useOpenAddSong';
import { ThemeStill } from '../../themes/ThemeStill';
import { useKitWebFonts } from '../../themes/kitWebFonts';
import { songPickerMessages as m } from './songPickerMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the picker; each part uses its own slice of it */
import styles from './SongPicker.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The three sample stills of the empty page (design t1c). */
const GHOST_THEMES: readonly ReleaseThemeId[] = ['dreamy', 'hype', 'neon'];
const NO_PALETTE: string[] = [];

interface StudioNoSongsProps {
  accountId: number;
}

/**
 * The Studio with no song in the catalog (design t1c): three small theme stills with the word
 * "Song", "Add a song to start", "+ An unreleased song" (the add-unreleased modal) and "Claim a
 * released song" (the catalog).
 */
const StudioNoSongsComponent: React.FC<StudioNoSongsProps> = ({ accountId }) => {
  const intl = useIntl();
  const router = useSMLRouter();
  useKitWebFonts();
  const openAddSong = useOpenAddSong('studio', accountId);
  const word = intl.formatMessage(m.emptyStill);

  return (
    <div className={styles.empty} data-testid="studio-no-songs">
      <div className={styles.ghosts} aria-hidden="true">
        {GHOST_THEMES.map((themeId) => (
          <ThemeStill
            key={themeId}
            themeId={themeId}
            fontOverride={null}
            palette={NO_PALETTE}
            artworkUrl={undefined}
            title={word}
            artist=""
            kicker=""
            mini
          />
        ))}
      </div>
      <h3>{intl.formatMessage(m.emptyTitle)}</h3>
      <p>{intl.formatMessage(m.emptyBody)}</p>
      <div className="d-flex flex-wrap justify-content-center gap-2">
        <SMLButton omitCap leftIcon={faPlus} onClick={openAddSong}>
          {intl.formatMessage(m.emptyAdd)}
        </SMLButton>
        <SMLButton outline omitCap onClick={() => router.push(SMLRoutes.catalog)}>
          {intl.formatMessage(m.emptyClaim)}
        </SMLButton>
      </div>
      <span className={styles.note}>{intl.formatMessage(m.emptyNote)}</span>
    </div>
  );
};

export const StudioNoSongs = TypedMemo(StudioNoSongsComponent);
